import {useForm} from '@tanstack/react-form';
import React from 'react';
import {Button} from '~/components/Button';
import {FormColumn, FormControls, FormRow, FormTitle, IntentText} from '~/components/Form';
import formStyle from '~/components/Form/index.module.scss';
import {IModalComponent, IModalComponentClose} from '~/components/Modal';
import {Intent} from '~/lib/intent';
import {MultiSelectAgency} from '~/modules/Clients/components/SelectAgency';
import {MultiSelectSeller} from '~/modules/Clients/components/SelectSeller';
import {SelectPreset} from '~/modules/Clients/components/SelectPreset';
import {defaultPresetBulkAssignment, IPresetBulkAssignment} from '~/modules/Clients/models/PresetBulkAssignment';
import {PresetSide} from '~/modules/Dictionary/models/Preset';

export interface IPresetBulkAssignFormActions {
    setStatus: (status: string | undefined) => void;
}

interface IPresetBulkAssignFormProps {
    side: PresetSide;
    onSubmit?: (values: IPresetBulkAssignment, formActions: IPresetBulkAssignFormActions, close: IModalComponentClose) => void | Promise<void>;
}

export const PresetBulkAssignForm: IModalComponent<IPresetBulkAssignFormProps> = props => {
    const closeModal = props.close ?? (() => undefined);
    const [status, setStatus] = React.useState<string>();
    const isDemand = props.side === 'demand';
    const resourceLabel = isDemand ? 'Agencies' : 'Sellers';

    const form = useForm({
        defaultValues: defaultPresetBulkAssignment,
        onSubmit: async ({value}) => {
            if (!props.onSubmit) return;
            await Promise.resolve(props.onSubmit(value, {setStatus}, closeModal));
        }
    });

    return <form.Subscribe selector={state => state}>
        {state => (
            <form className={formStyle.form} onSubmit={event => {
                event.preventDefault();
                void form.handleSubmit();
            }}>
                <FormTitle>Assign preset to {resourceLabel.toLowerCase()}</FormTitle>
                {status && <IntentText intent={Intent.DANGER}>{status}</IntentText>}
                <div className={formStyle.formBody}>
                    <FormRow caption={resourceLabel}>
                        <FormColumn>
                            <form.Field name='resourceIds'>
                                {fieldApi => {
                                    const selectProps = {
                                        value: fieldApi.state.value,
                                        onChange: (value: IPresetBulkAssignment['resourceIds']) => fieldApi.handleChange(value),
                                        placeholder: resourceLabel,
                                        searchable: true,
                                        selectAll: true,
                                        selectedItemsMaxHeight: 120,
                                        disabled: state.isSubmitting
                                    };
                                    return isDemand ? <MultiSelectAgency {...selectProps}/> : <MultiSelectSeller {...selectProps}/>;
                                }}
                            </form.Field>
                        </FormColumn>
                    </FormRow>
                    <FormRow caption='Preset'>
                        <FormColumn>
                            <form.Field name='presetId'>
                                {fieldApi => (
                                    <SelectPreset
                                        side={props.side}
                                        value={fieldApi.state.value}
                                        onChange={value => fieldApi.handleChange(value)}
                                        placeholder='Preset'
                                        searchable
                                        disabled={state.isSubmitting}
                                    />
                                )}
                            </form.Field>
                        </FormColumn>
                    </FormRow>
                </div>
                <FormControls>
                    <Button disabled={state.isSubmitting}
                        onClick={closeModal}
                    >Cancel</Button>
                    <Button type='submit'
                        disabled={state.isSubmitting || state.values.resourceIds.length === 0 || state.values.presetId == null}
                        intent={Intent.PRIMARY}
                        rightIcon={state.isSubmitting && <i className='fa fa-spinner fa-spin'/>}
                    >Apply</Button>
                </FormControls>
            </form>
        )}
    </form.Subscribe>;
};
